/**
 * Per-minute cost caps at the SDK layer (spec 68 §10.1).
 *
 * Each runtime instance enforces a span / log / event ceiling per rolling
 * 60s window. Overflow → drop + bump a `telemetry_dropped_total{kind}`
 * counter + (the facade) emits one summary `log.warn` per minute.
 *
 * | kind  | Node | Worker | Browser |
 * |-------|------|--------|---------|
 * | span  | 600  | 6000   | 60      |
 * | log   | 1200 | 6000   | 120     |
 * | event | 240  | 240    | 240     |
 */
import type { Runtime } from './runtime.js';

export type CapKind = 'span' | 'log' | 'event';

export interface CapLimits {
  span: number;
  log: number;
  event: number;
}

export const CAP_LIMITS: Record<Runtime, CapLimits> = {
  node: { span: 600, log: 1200, event: 240 },
  worker: { span: 6000, log: 6000, event: 240 },
  browser: { span: 60, log: 120, event: 240 },
};

const WINDOW_MS = 60_000;

export type Clock = () => number;

/** A single fixed-window counter. Not exported directly — used by CapSet. */
class WindowCounter {
  private windowStart: number;
  private count = 0;
  private dropped = 0;
  private droppedTotal = 0;

  constructor(
    private readonly limit: number,
    private readonly now: Clock,
  ) {
    this.windowStart = now();
  }

  private roll(t: number): void {
    if (t - this.windowStart >= WINDOW_MS) {
      this.windowStart = t;
      this.count = 0;
    }
  }

  /** Returns true if the item is within the cap; false if it must drop. */
  tryConsume(): boolean {
    const t = this.now();
    this.roll(t);
    if (this.count >= this.limit) {
      this.dropped += 1;
      this.droppedTotal += 1;
      return false;
    }
    this.count += 1;
    return true;
  }

  /** Drops accumulated since the last drain (for the per-minute summary). */
  drainDropped(): number {
    const d = this.dropped;
    this.dropped = 0;
    return d;
  }

  get lifetimeDropped(): number {
    return this.droppedTotal;
  }
}

/** A trio of window counters — one per cap kind. */
export class CapSet {
  private readonly counters: Record<CapKind, WindowCounter>;

  constructor(limits: CapLimits, now: Clock = Date.now) {
    this.counters = {
      span: new WindowCounter(limits.span, now),
      log: new WindowCounter(limits.log, now),
      event: new WindowCounter(limits.event, now),
    };
  }

  /** True → allowed; false → over cap, caller drops + accounts for it. */
  allow(kind: CapKind): boolean {
    return this.counters[kind].tryConsume();
  }

  /** Drain per-kind drop counts accumulated since the last drain. */
  drainDropped(): Record<CapKind, number> {
    return {
      span: this.counters.span.drainDropped(),
      log: this.counters.log.drainDropped(),
      event: this.counters.event.drainDropped(),
    };
  }

  lifetimeDropped(kind: CapKind): number {
    return this.counters[kind].lifetimeDropped;
  }
}

export function createCapSet(runtime: Runtime, now: Clock = Date.now): CapSet {
  return new CapSet(CAP_LIMITS[runtime], now);
}
